/**
 * Pulling a model's textures OUT of its GLB so the mesh can ship without them.
 *
 * The product reason: a game wants the character on screen fast. A rig-only GLB with no images is
 * a fraction of the size, so it loads first and the character appears untextured; the image files
 * are then fetched in parallel and attached with `applyTextures` (`src/runtime/textures.ts`) with
 * no geometry reload. UVs are NOT involved in any of this — `GLTFExporter.processMesh` writes every
 * geometry attribute regardless of the material (three r185, `GLTFExporter.js:1912-1975`), so
 * stripping is a MATERIAL operation and must never touch geometry. That is the whole reason the
 * round trip is safe.
 *
 * Two things this module refuses to do:
 *
 *  - **Re-encode.** The bytes come straight out of the source GLB's bufferViews. Going through a
 *    live `THREE.Texture` would mean canvas → `toBlob`, and a 500 KB JPEG comes back as a
 *    multi-megabyte PNG — the exact opposite of the point. It also means this module never needs a
 *    canvas, so it runs under jsdom where the textured export path cannot (that path dies in
 *    `putImageData`).
 *  - **Guess.** Every texture reference the glTF carries that this module does not map to a three
 *    material slot is named in `manifest.warnings` rather than silently dropped.
 *
 * The manifest is a NEW file kind. It is deliberately not the library schema: it describes one
 * model's materials, has no clips in it, and is never re-imported by the editor.
 */

import {
  TEXTURE_MANIFEST_SCHEMA_VERSION,
  type ManifestFactors,
  type ManifestImage,
  type ManifestMaterial,
  type ManifestSlot,
  type ManifestTextureTransform,
  type TextureManifest,
  type TextureSlot,
} from '../shared';
import { readGlbBinaryChunk, readGlbJsonChunk } from './glbInspect';

export interface ExtractedTextureFile {
  file: string;
  mimeType: string;
  bytes: Uint8Array;
}

export interface ExtractedTextures {
  manifest: TextureManifest;
  files: ExtractedTextureFile[];
  /** Sum of `files[].bytes.length` — what leaving the textures out of the GLB actually saves. */
  totalBytes: number;
}

export interface ExtractTexturesOptions {
  /** File name of the GLB the manifest describes, e.g. `"knight.glb"`. */
  modelFileName: string;
  /** Stem every image file name is prefixed with, so two characters can share a folder. */
  baseName: string;
  /**
   * Source material indices the exported model actually uses (`ImportedModel.materialSource`
   * values). Materials outside this set are skipped, so a file carrying a second scene's
   * materials does not make the export download images nothing can display.
   *
   * Omit to extract every material in the file.
   */
  usedMaterials?: ReadonlySet<number>;
}

// ---------------------------------------------------------------------------
// glTF JSON shapes (only the parts this module reads)
// ---------------------------------------------------------------------------

interface GltfTextureInfo {
  index?: unknown;
  texCoord?: unknown;
  scale?: unknown;
  strength?: unknown;
  extensions?: Record<string, unknown>;
}

interface GltfMaterial {
  name?: unknown;
  pbrMetallicRoughness?: {
    baseColorTexture?: GltfTextureInfo;
    metallicRoughnessTexture?: GltfTextureInfo;
    metallicFactor?: unknown;
    roughnessFactor?: unknown;
  };
  normalTexture?: GltfTextureInfo;
  occlusionTexture?: GltfTextureInfo;
  emissiveTexture?: GltfTextureInfo;
  emissiveFactor?: unknown;
  extensions?: Record<string, unknown>;
}

interface GltfTexture {
  sampler?: unknown;
  source?: unknown;
  extensions?: Record<string, unknown>;
}

interface GltfImage {
  uri?: unknown;
  mimeType?: unknown;
  bufferView?: unknown;
  name?: unknown;
}

interface GltfSampler {
  magFilter?: unknown;
  minFilter?: unknown;
  wrapS?: unknown;
  wrapT?: unknown;
}

interface GltfBufferView {
  buffer?: unknown;
  byteOffset?: unknown;
  byteLength?: unknown;
}

interface GltfBuffer {
  uri?: unknown;
  byteLength?: unknown;
}

const MIME_EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/avif': 'avif',
  'image/ktx2': 'ktx2',
  'image/vnd-ms.dds': 'dds',
};

/**
 * A file name every filesystem accepts. Intentionally a local copy of the rule rather than an
 * import of `safeFileName` from the editor's library controller: `src/importExport` is a layer
 * BELOW `src/editor` and must not reach up into it.
 */
function safeAssetName(name: string): string {
  let cleaned = '';
  for (const ch of name) {
    cleaned += /[\\/:*?"<>|]/.test(ch) || ch.charCodeAt(0) < 0x20 ? '_' : ch;
  }
  const trimmed = cleaned.trim();
  return trimmed.length > 0 ? trimmed : 'texture';
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

// ---------------------------------------------------------------------------
// Extraction
// ---------------------------------------------------------------------------

/**
 * Read every texture a GLB carries and return the manifest plus the image files, byte-exact.
 *
 * Never throws for a model that simply has no textures — that answers an empty manifest, which is
 * a perfectly good outcome and the caller says so instead of pretending it saved something. It
 * also never throws for a texture it cannot carry; those become `manifest.warnings`, because the
 * alternative is a GLB shipped without an image nobody was told about.
 *
 * Returns `null` only when `buffer` is not a readable GLB at all.
 */
export function extractTextures(
  buffer: ArrayBuffer,
  options: ExtractTexturesOptions,
): ExtractedTextures | null {
  const json = readGlbJsonChunk(buffer);
  if (json === null) return null;

  const warnings: string[] = [];

  const materialsJson = asArray<GltfMaterial>(json.materials);
  const texturesJson = asArray<GltfTexture>(json.textures);
  const imagesJson = asArray<GltfImage>(json.images);
  const samplersJson = asArray<GltfSampler>(json.samplers);
  const bufferViews = asArray<GltfBufferView>(json.bufferViews);
  const buffersJson = asArray<GltfBuffer>(json.buffers);

  /*
   * A buffer view names a BUFFER, and buffer 0 is only the GLB's BIN chunk by convention. A
   * perfectly legal file — and one `localOnly.ts` explicitly allows through the importer, since it
   * blocks external URLs but permits `data:` — stores its buffer as a base64 data URI and has no
   * BIN chunk at all. Slicing the BIN chunk for such a file would write a `.png` of whatever
   * happened to be at that offset: a "successful" export, a well-formed manifest, and an image
   * that refuses to decode in the game with nothing pointing at why.
   */
  const bin = readGlbBinaryChunk(buffer);
  const buffers = new Map<number, Uint8Array>();
  const bufferBytes = (index: number): Uint8Array | null => {
    const cached = buffers.get(index);
    if (cached) return cached;
    const declared = buffersJson[index];
    const uri = typeof declared?.uri === 'string' ? declared.uri : '';
    let bytes: Uint8Array | null = null;
    if (uri === '') bytes = index === 0 ? bin : null; // no uri ⇒ the GLB's own BIN chunk
    else if (uri.startsWith('data:')) bytes = decodeDataUri(uri);
    if (bytes) buffers.set(index, bytes);
    return bytes;
  };

  const images: ManifestImage[] = [];
  const files: ExtractedTextureFile[] = [];
  /** Source image index → index in `images`, so a shared image is written once. */
  const emitted = new Map<number, number>();

  function emitImage(sourceIndex: number): number | null {
    const already = emitted.get(sourceIndex);
    if (already !== undefined) return already;

    const image = imagesJson[sourceIndex];
    if (!image) {
      warnings.push(`Image ${sourceIndex} is referenced but not present in the file.`);
      return null;
    }

    const bytes = imageBytes(image, bufferViews, bufferBytes, sourceIndex, warnings);
    if (!bytes) return null;

    const mimeType =
      typeof image.mimeType === 'string' && image.mimeType.length > 0
        ? image.mimeType
        : sniffMimeType(bytes);
    const extension = MIME_EXTENSIONS[mimeType] ?? 'bin';
    /*
     * Named by INDEX, never by the glTF `images[].name`.
     *
     * Two images called "texture" — or two unnamed ones — would produce one file name, and the
     * second download would overwrite the first: the normal map served as the albedo, with the
     * manifest still looking perfectly consistent. This is the same class as the bone-name
     * landmine (`.claude/vault/glb-export-bone-names.md`), and the source index is unique by
     * construction. The base name keeps two characters from colliding in one asset folder.
     */
    const file = `${safeAssetName(options.baseName)}-tex${sourceIndex}.${extension}`;

    const declared = MIME_EXTENSIONS[mimeType];
    const sniffed = MIME_EXTENSIONS[sniffMimeType(bytes)];
    if (declared && sniffed && declared !== sniffed) {
      // Not fatal, but never silent: a slice that does not start with the magic bytes its
      // `mimeType` promises means the byte range was wrong, and the game would get an image that
      // simply refuses to decode with no clue why.
      warnings.push(
        `Image ${sourceIndex} is declared ${mimeType} but its bytes look like ` +
          `${sniffMimeType(bytes)}; it was written unchanged.`,
      );
    }

    const index = images.length;
    images.push({ file, mimeType, bytes: bytes.length, sourceIndex });
    files.push({ file, mimeType, bytes });
    emitted.set(sourceIndex, index);
    return index;
  }

  function readSlot(
    info: GltfTextureInfo | undefined,
    colorSpace: 'srgb' | 'linear',
    materialLabel: string,
  ): ManifestSlot | null {
    if (!info) return null;
    const textureIndex = asNumber(info.index);
    if (textureIndex === undefined) return null;

    const texture = texturesJson[textureIndex];
    if (!texture) {
      warnings.push(`${materialLabel}: texture ${textureIndex} is missing from the file.`);
      return null;
    }

    const sourceIndex = textureSource(texture, materialLabel, warnings);
    if (sourceIndex === null) return null;

    const image = emitImage(sourceIndex);
    if (image === null) return null;

    const slot: ManifestSlot = {
      image,
      colorSpace,
      texCoord: asNumber(info.texCoord) ?? 0,
    };

    const sampler = samplersJson[asNumber(texture.sampler) ?? -1];
    if (sampler) {
      const wrapS = asNumber(sampler.wrapS);
      const wrapT = asNumber(sampler.wrapT);
      const magFilter = asNumber(sampler.magFilter);
      const minFilter = asNumber(sampler.minFilter);
      if (wrapS !== undefined) slot.wrapS = wrapS;
      if (wrapT !== undefined) slot.wrapT = wrapT;
      if (magFilter !== undefined) slot.magFilter = magFilter;
      if (minFilter !== undefined) slot.minFilter = minFilter;
    }

    const transform = readTextureTransform(info);
    if (transform) slot.transform = transform;

    // `normalTexture.scale` and `occlusionTexture.strength` live on the texture reference, not on
    // the material, so a stripped GLB loses them with the reference. They ride in the manifest.
    const scale = asNumber(info.scale) ?? asNumber(info.strength);
    if (scale !== undefined) slot.scale = scale;

    return slot;
  }

  const materials: ManifestMaterial[] = [];

  materialsJson.forEach((material, id) => {
    // A GLB may describe materials no mesh in the exported scene uses — other scenes, or leftovers
    // an authoring tool never pruned. Extracting those would download megabytes of images for a
    // character that cannot show them.
    if (options.usedMaterials && !options.usedMaterials.has(id)) return;

    const name = typeof material.name === 'string' ? material.name : '';
    const label = `Material ${name ? `"${name}"` : `#${id}`}`;
    const slots: Partial<Record<TextureSlot, ManifestSlot>> = {};

    const pbr = material.pbrMetallicRoughness;
    const base = readSlot(pbr?.baseColorTexture, 'srgb', label);
    if (base) slots.map = base;

    // One glTF texture, two three slots: the ORM map is `metalnessMap` AND `roughnessMap`, with
    // the same sampler and channel. They are written separately so a reader never has to know
    // that rule, and they point at the SAME image entry — `applyTextures` loads it once.
    const orm = readSlot(pbr?.metallicRoughnessTexture, 'linear', label);
    if (orm) {
      slots.metalnessMap = orm;
      slots.roughnessMap = { ...orm };
    }

    const normal = readSlot(material.normalTexture, 'linear', label);
    if (normal) slots.normalMap = normal;

    const occlusion = readSlot(material.occlusionTexture, 'linear', label);
    if (occlusion) slots.aoMap = occlusion;

    const emissive = readSlot(material.emissiveTexture, 'srgb', label);
    if (emissive) slots.emissiveMap = emissive;

    readExtensionSlots(material, slots, readSlot, label, warnings);

    const factors = neutralizedFactors(material, slots);

    if (Object.keys(slots).length > 0 || factors) {
      const entry: ManifestMaterial = { id, name, slots };
      if (factors) entry.factors = factors;
      materials.push(entry);
    }
  });

  const manifest: TextureManifest = {
    schemaVersion: TEXTURE_MANIFEST_SCHEMA_VERSION,
    generator: 'intch-motion-kit',
    model: options.modelFileName,
    flipY: false,
    images,
    materials,
    warnings,
  };

  return {
    manifest,
    files,
    totalBytes: files.reduce((sum, file) => sum + file.bytes.length, 0),
  };
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * Which image a texture reads from. `texture.source` is the plain answer; a compressed or
 * alternate-codec texture hides it in an extension instead, and reading only `source` would make
 * a KTX2 model look like it has no textures at all.
 */
function textureSource(
  texture: GltfTexture,
  materialLabel: string,
  warnings: string[],
): number | null {
  const direct = asNumber(texture.source);
  if (direct !== undefined) return direct;

  for (const [name, value] of Object.entries(texture.extensions ?? {})) {
    const source = asNumber((value as { source?: unknown } | null)?.source);
    if (source !== undefined) {
      warnings.push(
        `${materialLabel}: texture uses ${name}; its image is carried as-is, and the game must ` +
          'load it with a matching loader.',
      );
      return source;
    }
  }

  warnings.push(`${materialLabel}: a texture reference names no image and was skipped.`);
  return null;
}

/**
 * The image's bytes. Three cases, and only one of them is the common one:
 *  - `bufferView` — the GLB case. A slice of the BIN chunk, copied verbatim.
 *  - a `data:` URI — some exporters write these even inside a GLB. Base64 in, bytes out.
 *  - any other `uri` — an image sitting next to the .gltf as its own file. The editor never
 *    fetched it (the importer runs on a local-only manager), so we have nothing to extract and
 *    say so rather than writing a zero-byte file.
 */
function imageBytes(
  image: GltfImage,
  bufferViews: GltfBufferView[],
  bufferBytes: (index: number) => Uint8Array | null,
  sourceIndex: number,
  warnings: string[],
): Uint8Array | null {
  const bufferViewIndex = asNumber(image.bufferView);
  if (bufferViewIndex !== undefined) {
    const view = bufferViews[bufferViewIndex];
    if (!view) {
      warnings.push(`Image ${sourceIndex} points at buffer view ${bufferViewIndex}, which is missing.`);
      return null;
    }
    const source = bufferBytes(asNumber(view.buffer) ?? 0);
    if (!source) {
      warnings.push(
        `Image ${sourceIndex} lives in a buffer this reader cannot reach (no binary chunk and ` +
          'no data URI).',
      );
      return null;
    }
    const offset = asNumber(view.byteOffset) ?? 0;
    const length = asNumber(view.byteLength) ?? 0;
    if (length <= 0 || offset + length > source.length) {
      warnings.push(`Image ${sourceIndex} has a buffer view that runs past the end of its buffer.`);
      return null;
    }
    // `slice`, not `subarray`: the result outlives the source buffer as a Blob, and a view would
    // pin the whole multi-megabyte GLB in memory for every image.
    return source.slice(offset, offset + length);
  }

  const uri = typeof image.uri === 'string' ? image.uri : '';
  if (uri.startsWith('data:')) {
    const bytes = decodeDataUri(uri);
    if (!bytes) {
      warnings.push(`Image ${sourceIndex} has a data URI that could not be decoded.`);
      return null;
    }
    return bytes;
  }

  if (uri.length > 0) {
    warnings.push(
      `Image ${sourceIndex} is an external file ("${uri}") that was never part of the GLB; ` +
        'ship it with your own assets.',
    );
    return null;
  }

  warnings.push(`Image ${sourceIndex} carries neither a buffer view nor a URI.`);
  return null;
}

/** `data:<mime>;base64,<payload>` → bytes. `null` for anything not base64-encoded. */
function decodeDataUri(uri: string): Uint8Array | null {
  const comma = uri.indexOf(',');
  if (comma < 0) return null;
  if (!uri.slice(5, comma).includes('base64')) return null;
  try {
    return base64ToBytes(uri.slice(comma + 1));
  } catch {
    return null;
  }
}

function base64ToBytes(base64: string): Uint8Array {
  // `atob` in the browser and in jsdom; `Buffer` when this runs under plain node.
  const decode = (globalThis as { atob?: (s: string) => string }).atob;
  if (decode) {
    const binary = decode(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  const nodeBuffer = (globalThis as { Buffer?: { from(s: string, e: string): Uint8Array } }).Buffer;
  if (nodeBuffer) return new Uint8Array(nodeBuffer.from(base64, 'base64'));
  throw new Error('No base64 decoder in this environment.');
}

/**
 * Mime type from the first bytes, for a file that declares none. Only the formats a glTF is
 * allowed to embed, plus webp, which several exporters write without a `mimeType`.
 */
function sniffMimeType(bytes: Uint8Array): string {
  if (bytes[0] === 0x89 && bytes[1] === 0x50) return 'image/png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'image/jpeg';
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[8] === 0x57) return 'image/webp';
  if (bytes[0] === 0xab && bytes[1] === 0x4b && bytes[2] === 0x54 && bytes[3] === 0x58) {
    return 'image/ktx2';
  }
  return 'application/octet-stream';
}

function readTextureTransform(info: GltfTextureInfo): ManifestTextureTransform | null {
  const ext = info.extensions?.KHR_texture_transform as
    | { offset?: unknown; scale?: unknown; rotation?: unknown }
    | undefined;
  if (!ext) return null;

  const transform: ManifestTextureTransform = {};
  const offset = ext.offset;
  if (Array.isArray(offset) && offset.length >= 2) {
    transform.offset = [Number(offset[0]) || 0, Number(offset[1]) || 0];
  }
  // glTF calls it `scale`; three calls the same thing `texture.repeat`. Renamed here so the
  // manifest speaks three's vocabulary and `applyTextures` never has to translate.
  const scale = ext.scale;
  if (Array.isArray(scale) && scale.length >= 2) {
    transform.repeat = [Number(scale[0]) || 0, Number(scale[1]) || 0];
  }
  const rotation = asNumber(ext.rotation);
  if (rotation !== undefined) transform.rotation = rotation;

  return Object.keys(transform).length > 0 ? transform : null;
}

/**
 * `KHR_materials_*` texture references, and the three.js material property each one lands on.
 *
 * These are here because the STRIPPER enumerates a material's textures rather than consulting a
 * list: whatever it finds, it removes. A clearcoat mask would therefore leave the GLB whether this
 * table exists or not — the only question is whether it also leaves the manifest, i.e. whether the
 * user loses it or gets it back. `GLTFLoader` builds all of these
 * (`GLTFLoader.js`, the `GLTFMaterials*Extension` classes), so the mapping is mechanical.
 */
const EXTENSION_SLOTS: ReadonlyArray<{
  extension: string;
  texture: string;
  slot: TextureSlot;
  colorSpace: 'srgb' | 'linear';
}> = [
  { extension: 'KHR_materials_clearcoat', texture: 'clearcoatTexture', slot: 'clearcoatMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_clearcoat', texture: 'clearcoatRoughnessTexture', slot: 'clearcoatRoughnessMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_clearcoat', texture: 'clearcoatNormalTexture', slot: 'clearcoatNormalMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_sheen', texture: 'sheenColorTexture', slot: 'sheenColorMap', colorSpace: 'srgb' },
  { extension: 'KHR_materials_sheen', texture: 'sheenRoughnessTexture', slot: 'sheenRoughnessMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_transmission', texture: 'transmissionTexture', slot: 'transmissionMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_volume', texture: 'thicknessTexture', slot: 'thicknessMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_iridescence', texture: 'iridescenceTexture', slot: 'iridescenceMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_iridescence', texture: 'iridescenceThicknessTexture', slot: 'iridescenceThicknessMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_anisotropy', texture: 'anisotropyTexture', slot: 'anisotropyMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_specular', texture: 'specularTexture', slot: 'specularIntensityMap', colorSpace: 'linear' },
  { extension: 'KHR_materials_specular', texture: 'specularColorTexture', slot: 'specularColorMap', colorSpace: 'srgb' },
];

/**
 * Fill the extension slots, and warn about any `*Texture` an extension carries that the table
 * above does not cover — that one really is lost, and the user hears about it at export time
 * rather than discovering a missing mask in the game.
 */
function readExtensionSlots(
  material: GltfMaterial,
  slots: Partial<Record<TextureSlot, ManifestSlot>>,
  readSlot: (
    info: GltfTextureInfo | undefined,
    colorSpace: 'srgb' | 'linear',
    label: string,
  ) => ManifestSlot | null,
  label: string,
  warnings: string[],
): void {
  const covered = new Set<string>();

  for (const entry of EXTENSION_SLOTS) {
    const ext = material.extensions?.[entry.extension] as Record<string, unknown> | undefined;
    if (!ext || typeof ext !== 'object') continue;
    covered.add(`${entry.extension}.${entry.texture}`);
    const info = ext[entry.texture] as GltfTextureInfo | undefined;
    if (!info) continue;
    const slot = readSlot(info, entry.colorSpace, label);
    if (slot) slots[entry.slot] = slot;
  }

  for (const [name, value] of Object.entries(material.extensions ?? {})) {
    if (!value || typeof value !== 'object') continue;
    for (const key of Object.keys(value as Record<string, unknown>)) {
      if (!key.endsWith('Texture') || covered.has(`${name}.${key}`)) continue;
      warnings.push(
        `${label}: ${name}.${key} is not a slot this exporter knows; that texture is left out of ` +
          'both the GLB and the manifest.',
      );
    }
  }
}

/**
 * The scalar values the stripped GLB must LIE about, and their real values for the manifest.
 *
 * `GLTFLoader` defaults `metalness` and `roughness` to 1.0 when the glTF omits the factors
 * (`GLTFLoader.js:3586-3587`) — which is exactly what an exporter writes when those values live in
 * the metallic-roughness texture. Keep the factors and strip the texture and the "fast placeholder"
 * character loads as a fully metallic, perfectly smooth black mirror. An `emissiveFactor` that was
 * being masked by an `emissiveTexture` has the same shape: glowing eyes become a white-hot
 * silhouette.
 *
 * So: for each channel whose texture is being stripped, the GLB gets a matte placeholder and the
 * manifest gets the truth. A channel with no texture keeps its authored factor and is not listed —
 * that value is not a lie and `applyTextures` must not touch it.
 */
function neutralizedFactors(
  material: GltfMaterial,
  slots: Partial<Record<TextureSlot, ManifestSlot>>,
): ManifestFactors | undefined {
  const factors: ManifestFactors = {};

  if (slots.metalnessMap) {
    factors.metalness = asNumber(material.pbrMetallicRoughness?.metallicFactor) ?? 1;
  }
  if (slots.roughnessMap) {
    factors.roughness = asNumber(material.pbrMetallicRoughness?.roughnessFactor) ?? 1;
  }
  if (slots.emissiveMap) {
    const emissive = material.emissiveFactor;
    const rgb = Array.isArray(emissive) ? emissive.map((c) => asNumber(c) ?? 0) : [0, 0, 0];
    // A black emissive factor is already the placeholder value, so recording it would make
    // `applyTextures` do work that changes nothing.
    if (rgb.some((c) => c !== 0)) factors.emissive = [rgb[0] ?? 0, rgb[1] ?? 0, rgb[2] ?? 0];
  }

  return Object.keys(factors).length > 0 ? factors : undefined;
}
